import { motion } from 'framer-motion';
import {
  Users,
  Target,
  TrendingUp,
  Lightbulb,
  ArrowRight,
  Phone,
  CheckCircle2,
  Monitor,
  AlertTriangle,
  Shield,
  Globe,
  Calendar,
  MessageSquare
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const services = [
  {
    icon: Lightbulb,
    title: 'Analisis & Brainstorming Strategi Bisnis',
    description: 'Mendapatkan perspektif baru dan insight mendalam untuk strategi bisnis Anda'
  },
  {
    icon: Target,
    title: 'Pemecahan Masalah Bisnis',
    description: 'Problem solving untuk mengatasi tantangan bisnis yang Anda hadapi'
  },
  {
    icon: TrendingUp,
    title: 'Perencanaan & Strategi Pengembangan Usaha',
    description: 'Menyusun roadmap pertumbuhan usaha pada berbagai skala'
  },
  {
    icon: Users,
    title: 'Advisory & Coaching',
    description: 'Pengembangan sistem usaha dengan pendampingan profesional'
  },
  {
    icon: Monitor,
    title: 'Business Automation',
    description: 'Konsultasi automasi bisnis berbasis tools & workflow untuk mengurangi human error, meningkatkan efisiensi, dan mempercepat scale.'
  },
  {
    icon: AlertTriangle,
    title: 'Fraud Detection',
    description: 'Identifikasi dan mitigasi risiko kecurangan dalam bisnis'
  },
  {
    icon: Globe,
    title: 'Market Entry Strategy & Ekspansi Bisnis',
    description: 'Strategi memasuki pasar baru dan ekspansi bisnis'
  },
  {
    icon: Shield,
    title: 'Crisis Management & Exit Strategy',
    description: 'Strategi menghadapi penurunan bisnis, restrukturisasi hutang-piutang, dan perencanaan exit berbasis risk management agar kerugian terkendali dan aset terjaga.'
  },
  {
    icon: CheckCircle2,
    title: 'Business Check-Up',
    description: 'Audit komprehensif kondisi bisnis (operasional, keuangan, market, strategi) untuk mengidentifikasi bottleneck dan peluang pertumbuhan secara objektif.'
  }
];

export function BusinessAdvisory() {
  const { ref: heroRef, isInView: heroInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: servicesRef, isInView: servicesInView } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative pt-32 pb-20 overflow-hidden"
      >
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/business-consultant-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="relative max-w-7xl mx-auto section-padding z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl font-display font-bold text-white/10">05</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Business Advisory
            </h1>

            <p className="text-xl text-white/70 mb-4 leading-relaxed">
              <strong className="text-primary-400">Business Advisory & Partner</strong>
            </p>

            <p className="text-white/60 mb-8">
              Konsultasi Strategis untuk Pertumbuhan Bisnis Anda secara Berkala. 
              Layanan konsultasi kami tersedia untuk <strong className="text-primary-400">UMKM & perusahaan</strong> yang ingin mendapatkan insight memulai usaha, 
              strategis, mengatasi tantangan bisnis, atau menyusun roadmap pertumbuhan usaha pada berbagai skala.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Layanan Advisory Kami
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Dapatkan insight mendalam & solusi praktis melalui sesi konsultasi dengan kami!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                <p className="text-white/60 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Format Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/consultant-meeting-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="max-w-7xl mx-auto section-padding relative z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-primary-400" />
              </div>
            </div>
            
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4 text-center">
              Format Meeting
            </h2>
            
            <div className="flex items-center justify-center gap-2 mb-6">
              <Monitor className="w-5 h-5 text-primary-400" />
              <span className="text-white/80 text-lg">Online Meeting</span>
            </div>
            
            <p className="text-white/60 text-center mb-6">
              Google Meet, Zoom, dan platform lainnya
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <div className="bg-white/5 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Calendar className="w-5 h-5 text-primary-400" />
                  <h3 className="text-lg font-semibold text-white">Jadwal Fleksibel</h3>
                </div>
                <p className="text-white/60 text-sm">
                  Sesuai kesepakatan dengan klien untuk memastikan kenyamanan dan produktivitas
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="w-5 h-5 text-primary-400" />
                  <h3 className="text-lg font-semibold text-white">Untuk Berbagai Skala</h3>
                </div>
                <p className="text-white/60 text-sm">
                  UMKM, startup, hingga perusahaan skala menengah dan besar
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 text-center"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary-400" />
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              Butuh Perspektif Baru?
            </h2>

            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              Dapatkan insight mendalam & solusi praktis melalui sesi konsultasi dengan kami!
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
